// LoginLanding — high-conversion landing section shown BELOW the login form.
// Iter 121: psychological grab content for visitors who land on /login from
// social ads, WhatsApp shares, or peer referrals. Mobile-first, fully
// responsive (single column on phones, 2-3 columns on tablets/desktops).
//
// Sections (top → bottom):
//   1. Live social-proof strip (active schools + hours saved)
//   2. "Why MDM Sahayak" — Time / Mind / Health benefit cards
//   3. "How it works" — 3 simple steps
//   4. Live plan teaser (fetched from /api/subscriptions/plans)
//   5. Testimonials carousel (Odia + English)
//   6. FAQ accordion
//   7. Final CTA strip pointing to /register

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  Sparkles, Clock, Brain, Heart, Star, Shield, Zap, Users,
  CheckCircle2, ChevronDown, IndianRupee, Crown, MessageCircle,
} from "lucide-react";
import { buildSupportWhatsAppUrl } from "@/lib/supportContact";
import OfferCountdownBig from "@/components/poshan/OfferCountdownBig";
import GetNowButton from "@/components/poshan/GetNowButton";
import FomoCounter from "@/components/poshan/FomoCounter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Visual mapping per plan key
const PLAN_VISUAL = {
  mdcf_monthly: { Icon: Star, ring: "ring-blue-300", chip: "from-blue-50 to-indigo-50", accent: "text-blue-700" },
  mdcf_yearly: { Icon: Crown, ring: "ring-purple-400", chip: "from-purple-50 to-pink-50", accent: "text-purple-700", popular: true },
  all_reports_yearly: { Icon: Sparkles, ring: "ring-amber-400", chip: "from-amber-50 to-orange-50", accent: "text-amber-700" },
};

const STEPS = [
  {
    n: 1,
    title_en: "Sign up in 60 seconds",
    title_od: "୬୦ ସେକେଣ୍ଡରେ Account ତିଆରି",
    body_en: "Enter your mobile + school details. No paperwork.",
    body_od: "ମୋବାଇଲ୍ + ସ୍କୁଲ ବିବରଣୀ ଭର୍ତ୍ତି କରନ୍ତୁ।",
  },
  {
    n: 2,
    title_en: "Complete School Setup",
    title_od: "ସ୍କୁଲ Setup ସମ୍ପୂର୍ଣ୍ଣ କରନ୍ତୁ",
    body_en: "Add school identity, vendor, CCH and year-wise enrolment — once.",
    body_od: "ଥରେ ସମସ୍ତ ବିବରଣୀ ଭର୍ତ୍ତି କରନ୍ତୁ।",
  },
  {
    n: 3,
    title_en: "Generate reports in 3 minutes",
    title_od: "୩ ମିନିଟ୍ ରେ Report ତିଆରି",
    body_en: "MDCF, UC, Hand Receipts — auto-filled in Odia + English.",
    body_od: "MDCF, UC, Hand Receipts ଆପେ ଆପେ ତିଆରି।",
  },
];

const TESTIMONIALS = [
  {
    name: "Headmaster, Govt UPS",
    district: "Khordha",
    quote_od: "ପୂର୍ବେ ୬ ଘଣ୍ଟା ଲାଗୁଥିଲା MDCF ତିଆରି କରିବାକୁ। ଏବେ କେବଳ ୧୦ ମିନିଟ୍ ରେ Print ଯାଇଯାଉଛି।",
    quote_en: "MDCF used to take 6 hours. Now it prints in just 10 minutes.",
  },
  {
    name: "MDM Convenor",
    district: "Cuttack",
    quote_od: "Hand Receipt ଓ UC Report ଗୁଡିକ Auto ତିଆରି ହୋଇ ସମୟ ବହୁତ ବଞ୍ଚାଯାଉଛି।",
    quote_en: "Hand Receipts + UC reports auto-generate. Saves so much time.",
  },
  {
    name: "Acting Headmaster",
    district: "Ganjam",
    quote_od: "Daily Entry ଓ Fund Management ବହୁତ ସରଳ। ଗୋଟିଏ Click ରେ Monthly MDCF।",
    quote_en: "Daily Entry + Fund Management is so simple. One click for Monthly MDCF.",
  },
];

const FAQS = [
  {
    q_en: "Is this an Odisha Govt portal?",
    q_od: "ଏହା କ’ଣ Odisha Govt Portal?",
    a_en: "No. MDM Sahayak is an independent SaaS productivity tool for school staff. It does not replace any official government portal — it complements your workflow by speeding up calculations and printing.",
    a_od: "ନୁହେଁ। MDM Sahayak ଏକ ସ୍ୱାଧୀନ SaaS productivity tool — ସରକାରୀ portal କୁ replace କରେନାହିଁ, ବରଂ କାର୍ଯ୍ୟକୁ ଶୀଘ୍ର କରେ।",
  },
  {
    q_en: "How long does it take to generate a Monthly MDCF?",
    q_od: "Monthly MDCF ତିଆରି କରିବାକୁ କେତେ ସମୟ ଲାଗେ?",
    a_en: "Less than 3 minutes after your Daily Entry data is filled — Odia + English versions are generated together.",
    a_od: "Daily Entry ଭର୍ତ୍ତି ହୋଇଗଲେ ୩ ମିନିଟ୍ ରୁ କମ୍ — Odia + English ଦୁଇଟି ଏକ ସମୟରେ ତିଆରି।",
  },
  {
    q_en: "Can I cancel anytime?",
    q_od: "ମୁଁ କ’ଣ ଯେକୌଣସି ସମୟରେ Cancel କରିପାରିବି?",
    a_en: "Yes. There's no auto-renewal. Plans expire on their validity date — you simply choose whether to renew.",
    a_od: "ହଁ। Auto-renew ନାହିଁ। Plan ର ସମୟ ଶେଷ ହୋଇଗଲେ ଆପଣ ନୂଆ କରି Renew କରିପାରନ୍ତି।",
  },
  {
    q_en: "Is my school data safe?",
    q_od: "ମୋ ସ୍କୁଲ Data କ’ଣ ସୁରକ୍ଷିତ?",
    a_en: "Yes. Each school's data is fully isolated via multi-tenant security. Only the registered headmaster can access their own school.",
    a_od: "ହଁ। ପ୍ରତି ସ୍କୁଲ ର Data ଅଲଗା ଅଲଗା ସୁରକ୍ଷିତ — କେବଳ Register ହୋଇଥିବା HM ହିଁ ଦେଖିପାରିବେ।",
  },
];

function PlanTeaser({ plan, onPick }) {
  const v = PLAN_VISUAL[plan.key] || PLAN_VISUAL.mdcf_monthly;
  const Icon = v.Icon;
  const hasOffer = (plan.original_price_rupees || 0) > (plan.amount_rupees || 0);
  return (
    <div
      className={`relative w-full overflow-hidden rounded-2xl border-2 bg-gradient-to-br ${v.chip} p-5 text-left shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg ${v.popular ? `ring-2 ${v.ring} ring-offset-2` : `border-transparent`}`}
      data-testid={`landing-plan-${plan.key}`}
      aria-label={`Choose ${plan.label} — ₹${plan.amount_rupees}`}
    >
      {/* iter125 — limited-time offer badge */}
      {hasOffer && plan.offer_badge && (
        <span className="absolute left-0 top-3 rounded-r-full bg-red-600 px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.12em] text-white shadow-lg ring-2 ring-red-400/50" data-testid={`landing-plan-offer-${plan.key}`}>
          {plan.offer_badge}
        </span>
      )}
      {v.popular && (
        <span className="absolute right-3 top-3 rounded-full bg-purple-600 px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-[0.12em] text-white shadow">
          ⭐ Most Popular
        </span>
      )}
      <Icon className={`h-8 w-8 ${v.accent} ${hasOffer ? "mt-4" : ""}`} />
      <h3 className="mt-3 text-base font-bold text-ink-900">{plan.label}</h3>
      <div className="mt-2 flex items-baseline gap-2">
        <span className={`flex items-center text-3xl font-bold ${v.accent}`}>
          <IndianRupee className="h-5 w-5" />{plan.amount_rupees}
        </span>
        {hasOffer && (
          <span className="flex items-center text-base font-semibold text-rose-600 line-through decoration-rose-500 decoration-2" data-testid={`landing-plan-strike-${plan.key}`}>
            <IndianRupee className="h-3.5 w-3.5" />{plan.original_price_rupees}
          </span>
        )}
        <span className="text-xs text-ink-700">
          / {plan.validity_days === 30 ? "month" : "year"}
        </span>
      </div>
      {hasOffer && plan.offer_label && (
        <p className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-red-50 px-2 py-0.5 text-[0.65rem] font-semibold text-red-700 ring-1 ring-red-200">
          ⏰ {plan.offer_label}
        </p>
      )}
      {plan.psychological_cta && (
        <p className="mt-2 text-xs italic text-ink-700">"{plan.psychological_cta}"</p>
      )}
      {plan.time_saved_per_month && (
        <p className="mt-3 inline-flex items-center gap-1 rounded-full bg-white/80 px-2.5 py-1 text-[0.7rem] font-semibold text-emerald-800">
          <Clock className="h-3 w-3" /> {plan.time_saved_per_month}
        </p>
      )}
      {/* iter127 — big eye-catching countdown directly under the plan */}
      <OfferCountdownBig endsAt={plan.offer_ends_at} testId={`landing-countdown-${plan.key}`} />
      {/* iter127 — psychological "Get Now" CTA */}
      <GetNowButton
        onClick={() => onPick(plan)}
        priceRupees={plan.amount_rupees}
        testId={`landing-get-now-${plan.key}`}
      />
      {/* iter128 — FOMO social-proof + scarcity counter */}
      <FomoCounter planKey={plan.key} testId={`landing-fomo-${plan.key}`} />
    </div>
  );
}

function FaqItem({ item, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <details
      className="group rounded-xl border border-border bg-white p-4 transition-all open:shadow-md"
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
      data-testid="landing-faq-item"
    >
      <summary className="flex cursor-pointer list-none items-start justify-between gap-3 text-sm font-semibold text-ink-900">
        <div>
          <p>{item.q_en}</p>
          <p className="mt-0.5 text-xs font-normal text-ink-700">{item.q_od}</p>
        </div>
        <ChevronDown className={`mt-0.5 h-4 w-4 shrink-0 text-ink-700 transition-transform ${open ? "rotate-180" : ""}`} />
      </summary>
      <div className="mt-3 space-y-2 text-sm text-ink-800">
        <p>{item.a_en}</p>
        <p className="text-xs text-ink-700">{item.a_od}</p>
      </div>
    </details>
  );
}

export default function LoginLanding() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  // No backend route exists for these; keep the long-standing display values.
  const stats = { schools: 26, hoursSaved: 1200 };

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/subscriptions/plans`);
        setPlans(r.data?.subscription_plans || []);
      } catch (e) { /* ignore — landing degrades gracefully */ }
    })();
  }, []);

  // Picking a plan from the landing page sends the visitor to the registration
  // flow with their intended plan stashed in sessionStorage. After they create
  // an account + complete setup, the Choose-Plan screen reads this hint and
  // pre-selects the same plan so they don't have to pick again.
  const pickPlan = (plan) => {
    try {
      sessionStorage.setItem("mdm.intendedPlan", plan.key);
      sessionStorage.setItem("mdm.intendedPlanLabel", plan.label);
    } catch (e) { /* sessionStorage may be blocked — non-fatal */ }
    navigate(`/register?plan=${encodeURIComponent(plan.key)}`);
  };

  const whatsappCTA = () => {
    const url = buildSupportWhatsAppUrl("Hi MDM Sahayak Team — I'd like to start using the platform. Please guide me.");
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <section className="mt-12 space-y-10 sm:mt-16" data-testid="login-landing">
      {/* 1) Social-proof strip */}
      <div className="rounded-3xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-amber-50 p-5 sm:p-8" data-testid="landing-social-proof">
        <p className="text-center text-[0.7rem] font-bold uppercase tracking-[0.18em] text-emerald-700">Trusted by schools across Odisha</p>
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="text-center">
            <p className="text-3xl font-bold text-emerald-700 sm:text-4xl"><Users className="mx-auto mb-1 h-6 w-6" />{stats.schools}+</p>
            <p className="mt-1 text-[0.7rem] font-semibold uppercase tracking-wide text-ink-700">Active Schools</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-amber-700 sm:text-4xl"><Clock className="mx-auto mb-1 h-6 w-6" />{stats.hoursSaved}+</p>
            <p className="mt-1 text-[0.7rem] font-semibold uppercase tracking-wide text-ink-700">Hours Saved</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-violet-700 sm:text-4xl"><Zap className="mx-auto mb-1 h-6 w-6" />3 min</p>
            <p className="mt-1 text-[0.7rem] font-semibold uppercase tracking-wide text-ink-700">to Generate Reports</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-700 sm:text-4xl"><Shield className="mx-auto mb-1 h-6 w-6" />100%</p>
            <p className="mt-1 text-[0.7rem] font-semibold uppercase tracking-wide text-ink-700">Data Privacy</p>
          </div>
        </div>
      </div>

      {/* 2) Why MDM Sahayak — Time / Mind / Health */}
      <div data-testid="landing-why-us">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-ink-900 sm:text-3xl">Why headmasters love MDM Sahayak</h2>
          <p className="mt-2 text-sm text-ink-700">କାହିଁକି ସ୍କୁଲ HM ଗଣ MDM Sahayak ବ୍ୟବହାର କରୁଛନ୍ତି</p>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/60 p-5">
            <Clock className="h-9 w-9 text-emerald-600" />
            <h3 className="mt-3 text-base font-bold text-ink-900">Reclaim your time</h3>
            <p className="mt-1 text-xs text-emerald-900/90">Save <b>50-100+ hours/year</b> on MDM paperwork.</p>
            <p className="mt-1 text-xs text-ink-700">ବର୍ଷକୁ ୫୦-୧୦୦+ ଘଣ୍ଟା ବଞ୍ଚାନ୍ତୁ।</p>
          </div>
          <div className="rounded-2xl border-2 border-violet-200 bg-violet-50/60 p-5">
            <Brain className="h-9 w-9 text-violet-600" />
            <h3 className="mt-3 text-base font-bold text-ink-900">Peace of mind</h3>
            <p className="mt-1 text-xs text-violet-900/90">No more late-night calculation panic. <b>Auto-validated</b> entries.</p>
            <p className="mt-1 text-xs text-ink-700">ଆଉ Deadline ର ଚିନ୍ତା ନୁହେଁ।</p>
          </div>
          <div className="rounded-2xl border-2 border-rose-200 bg-rose-50/60 p-5">
            <Heart className="h-9 w-9 text-rose-600" />
            <h3 className="mt-3 text-base font-bold text-ink-900">Better health</h3>
            <p className="mt-1 text-xs text-rose-900/90">Less <b>register writing</b> = fewer headaches and wrist pain.</p>
            <p className="mt-1 text-xs text-ink-700">Register ଲେଖିବାରୁ ମୁକ୍ତି।</p>
          </div>
        </div>
      </div>

      {/* 3) How it works */}
      <div data-testid="landing-how-it-works">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-ink-900 sm:text-3xl">3 simple steps</h2>
          <p className="mt-2 text-sm text-ink-700">୩ଟି ସରଳ Step</p>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {STEPS.map((s) => (
            <div key={s.n} className="relative rounded-2xl border border-border bg-white p-5 shadow-sm">
              <div className="absolute -top-3 left-5 flex h-8 w-8 items-center justify-center rounded-full bg-clay font-bold text-white shadow-md">
                {s.n}
              </div>
              <h3 className="mt-3 text-base font-bold text-ink-900">{s.title_en}</h3>
              <p className="mt-1 text-xs text-ink-700">{s.title_od}</p>
              <p className="mt-2 text-sm text-ink-800">{s.body_en}</p>
              <p className="mt-1 text-xs italic text-ink-700">{s.body_od}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 4) Plan teaser */}
      {plans.length > 0 && (
        <div data-testid="landing-plans">
          <div className="text-center">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-ink-900 sm:text-3xl">Pick a plan when you're ready</h2>
            <p className="mt-2 text-sm text-ink-700">Start free, upgrade only when you need reports.</p>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {plans.map((p) => <PlanTeaser key={p.key} plan={p} onPick={pickPlan} />)}
          </div>
        </div>
      )}

      {/* 5) Testimonials */}
      <div data-testid="landing-testimonials">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-ink-900 sm:text-3xl">What schools are saying</h2>
          <p className="mt-2 text-sm text-ink-700">ସ୍କୁଲ HM ଗଣ କଣ କହୁଛନ୍ତି</p>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="rounded-2xl border border-border bg-white p-5 shadow-sm">
              <div className="flex text-amber-500">
                {[1, 2, 3, 4, 5].map((s) => <Star key={s} className="h-4 w-4 fill-amber-500" />)}
              </div>
              <p className="mt-3 text-sm leading-relaxed text-ink-800">"{t.quote_od}"</p>
              <p className="mt-2 text-xs italic text-ink-700">"{t.quote_en}"</p>
              <p className="mt-4 text-xs font-bold text-ink-900">— {t.name}, {t.district}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 6) FAQ */}
      <div data-testid="landing-faq">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-ink-900 sm:text-3xl">Frequently asked</h2>
          <p className="mt-2 text-sm text-ink-700">ସାଧାରଣତଃ ପଚରା ଯାଉଥିବା ପ୍ରଶ୍ନ</p>
        </div>
        <div className="mt-6 space-y-3">
          {FAQS.map((f, i) => <FaqItem key={i} item={f} defaultOpen={i === 0} />)}
        </div>
      </div>

      {/* 7) Final CTA */}
      <div className="overflow-hidden rounded-3xl bg-gradient-to-br from-clay via-amber-600 to-rose-600 p-6 text-center text-white shadow-xl sm:p-10" data-testid="landing-final-cta">
        <Sparkles className="mx-auto h-10 w-10" />
        <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight sm:text-3xl">
          Ready to save 50+ hours this year?
        </h2>
        <p className="mt-2 text-sm opacity-95">ସମ୍ପୂର୍ଣ୍ଣ ବର୍ଷ ୫୦+ ଘଣ୍ଟା ବଞ୍ଚାନ୍ତୁ — Setup ୨ ମିନିଟ୍ ରେ ସମ୍ପୂର୍ଣ୍ଣ।</p>
        <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link to="/register" data-testid="landing-cta-register">
            <Button size="lg" className="w-full bg-white text-clay shadow-md hover:bg-white/90 sm:w-auto">
              <CheckCircle2 className="mr-2 h-5 w-5" /> Create your free account
            </Button>
          </Link>
          <Button
            size="lg"
            variant="outline"
            onClick={whatsappCTA}
            className="w-full border-white bg-white/10 text-white hover:bg-white/20 sm:w-auto"
            data-testid="landing-cta-whatsapp"
          >
            <MessageCircle className="mr-2 h-5 w-5" /> Talk to us on WhatsApp
          </Button>
        </div>
      </div>
    </section>
  );
}
